import { useEffect, useRef, useState } from "react";
import type { Category } from "../types/domain";
import { categoryPath, childrenOf } from "../lib/categories";

type Props = {
  categories: Category[];
  value: number | null;
  onChange: (categoryId: number | null) => void;
};

export function CategoryCascadeSelect({ categories, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<Set<number>>(() => new Set());
  const rootRef = useRef<HTMLDivElement | null>(null);
  const path = categoryPath(categories, value);
  const selected = path[path.length - 1] ?? null;
  const selectedLabel = path.length ? path.map((category) => category.name).join("/") : "Во всех категориях";
  const selectedIds = new Set(path.map((category) => category.id));

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const chooseCategory = (category: Category) => {
    const hasChildren = childrenOf(categories, category.id).length > 0;
    if (hasChildren && selectedIds.has(category.id)) {
      setCollapsedCategoryIds((current) => {
        const next = new Set(current);
        if (next.has(category.id)) next.delete(category.id);
        else next.add(category.id);
        return next;
      });
      onChange(category.id);
      return;
    }
    setCollapsedCategoryIds(new Set());
    onChange(category.id);
    if (!hasChildren) setOpen(false);
  };

  const renderLevel = (parentId: number | null, depth = 0) => {
    const parentCategory = parentId ? categories.find((category) => category.id === parentId) : null;
    return (
      <div className="category-cascade-options" data-depth={depth}>
        {parentCategory ? (
          <button
            className="pm-select-option pm-menu-option category-cascade-all category-cascade-all-in"
            type="button"
            role="option"
            aria-selected={value === parentCategory.id}
            onClick={() => {
              setCollapsedCategoryIds(new Set());
              onChange(parentCategory.id);
              setOpen(false);
            }}
          >
            Все в {parentCategory.name}
          </button>
        ) : null}
        {childrenOf(categories, parentId).map((category) => {
          const hasChildren = childrenOf(categories, category.id).length > 0;
          const expanded = selectedIds.has(category.id) && hasChildren && !collapsedCategoryIds.has(category.id);
          return (
            <div className="category-cascade-branch" key={category.id}>
              <button
                className="pm-select-option pm-menu-option category-cascade-option"
                type="button"
                role="option"
                aria-selected={selectedIds.has(category.id)}
                onClick={() => chooseCategory(category)}
              >
                <span>{category.name}</span>
                {hasChildren ? <span className="category-cascade-arrow" aria-hidden="true">›</span> : null}
              </button>
              {expanded ? renderLevel(category.id, depth + 1) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="pm-select category-cascade-select" ref={rootRef}>
      <button className="pm-select-trigger" type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span className="pm-select-value" title={selected?.name ? selectedLabel : undefined}>{selectedLabel}</span>
        <span className="pm-chevron" aria-hidden="true" />
      </button>
      {open ? (
        <div className="pm-select-menu category-cascade-menu" role="listbox">
          <button
            className="pm-select-option pm-menu-option category-cascade-all"
            type="button"
            role="option"
            aria-selected={!value}
            onClick={() => {
              setCollapsedCategoryIds(new Set());
              onChange(null);
              setOpen(false);
            }}
          >
            Во всех категориях
          </button>
          {renderLevel(null)}
        </div>
      ) : null}
    </div>
  );
}
